import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect } from 'react';
import useMsal from '../../../auth/useMsal';
import { useAppStore } from '../../../context/AppContext';
import { useAuthStore } from '../../../context/AuthContext';
import { SidePanelModulEnum } from '../../../enums/sidePanelModulEnum';
import { About, ClearCacheWrapper, Wrapper } from './SidePanelPopup.styles';
import { useRef } from 'react';
import getReleaseNotesContext from '../../../context/ReleaseNotesContext';
import { useVesselStore } from '../../../context/VesselContext';
import { Curtain } from '../../../App.styles';
import useApiLoaderHelper from '../../../utils/useApiLoaderHelper';
import SpinnerLoaderComponent from '../loader/SpinnerLoader';
import { RelativeWrapper } from '../../../styles/general.style';
import { useSWService } from '../../../api/sw/useSWService';
import FeaturesSelection from './FeaturesSelection';
import UserRoleSelection from './UserRoleSelection';
import ResolutionSelectionComponent from './ResolutionSelection';
import { OfflineSelectionComponent } from './OfflineSelection';
import { RoleTypeEnum } from 'src/enums/roleTypeEnum';

const SidePanelPopupComponent: React.FC = () => {
    const { isLoadingEndpoint } = useApiLoaderHelper();
    const { appsMenu, visibleSidePanelModuleIndex, setVisibleSidePanelModuleIndex } = useAppStore();
    const { loggedInRole, role, loggedInUser } = useAuthStore();
    const { logoutAzAd } = useMsal();
    const { clearCache } = useSWService();
    const logout = () => {
        logoutAzAd();
    };
    const ref = useRef<HTMLInputElement>(null);
    const releaseNotes = useContext(getReleaseNotesContext());
    const { selectedVessel } = useVesselStore();

    useEffect(() => {
        const checkIfClickedOutside = (event) => {
            if (visibleSidePanelModuleIndex > 0 && ref.current && !ref.current.contains(event.target)) {
                setVisibleSidePanelModuleIndex(-1);
            }
        };

        document.addEventListener('mousedown', checkIfClickedOutside);

        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [visibleSidePanelModuleIndex]);

    const clearCacheAndLocalStorage = async () => {
        // Preserve user preferences
        const userPreferences = {
            operationListColumns: localStorage.getItem('operationListColumns'),
            operationListColumnWidths: localStorage.getItem('operationListColumnWidths'),
            operationListColumnOrder: localStorage.getItem('operationListColumnOrder'),
            operationListSortColumn: localStorage.getItem('operationListSortColumn'),
            isOperationListSortDesc: localStorage.getItem('isOperationListSortDesc'),
            // Add other user preferences as needed
        };

        localStorage.clear();

        // Restore user preferences
        Object.entries(userPreferences).forEach(([key, value]) => {
            if (value !== null) {
                localStorage.setItem(key, value);
            }
        });

        await clearCache();
        window.location.reload();
    };

    return (
        <>
            <Curtain></Curtain>
            <Wrapper ref={ref}>
                {visibleSidePanelModuleIndex === SidePanelModulEnum.AppsMenu && appsMenu && (
                    <>
                        <h2>
                            <i onClick={() => setVisibleSidePanelModuleIndex(SidePanelModulEnum.NONE)}>
                                <FontAwesomeIcon icon="times" />
                            </i>
                            My Odfjell Apps
                        </h2>
                        <RelativeWrapper>
                            <>
                                {isLoadingEndpoint('getAppsDataHtml') && (
                                    <SpinnerLoaderComponent
                                        message={isLoadingEndpoint('getAppsDataHtml').message}
                                        clearDefaultText={true}
                                        isFeatureSpinner={true}
                                    />
                                )}
                            </>
                            <div dangerouslySetInnerHTML={{ __html: appsMenu.html }} />
                        </RelativeWrapper>
                    </>
                )}

                {visibleSidePanelModuleIndex === SidePanelModulEnum.About && loggedInUser && role && (
                    <About>
                        <h2>
                            <i onClick={() => setVisibleSidePanelModuleIndex(SidePanelModulEnum.NONE)}>
                                <FontAwesomeIcon icon="times" />
                            </i>
                            About
                        </h2>
                        <div className="user-info-panel">
                            <div className="release-notes-container">
                                <h3>Release notes</h3>
                                <textarea className="release-notes" disabled={true} value={releaseNotes} />
                            </div>
                            <div className="top-wrapper">
                                <h3>
                                    {loggedInUser?.fullName} ({loggedInRole?.roleType.toString()})
                                </h3>
                                <UserRoleSelection></UserRoleSelection>
                                <FeaturesSelection></FeaturesSelection>
                                <ResolutionSelectionComponent></ResolutionSelectionComponent>
                                {loggedInRole?.roleType === RoleTypeEnum.Admin && <OfflineSelectionComponent />}

                                {(loggedInUser?.fullName === 'Vegard Marken' ||
                                    loggedInUser?.fullName === 'Arnel Pinedo' ||
                                    loggedInUser?.fullName === 'Florian Pujol') &&
                                    selectedVessel?.vesselName === 'Bow Summer' && (
                                        <img className={'burger'} src={'assets/burger.jpg'} alt="Burger" loading="lazy" />
                                    )}
                            </div>
                            <div className="bottom-wrapper">
                                <button className="logout" onClick={logout}>
                                    Sign out
                                </button>
                            </div>
                        </div>
                    </About>
                )}

                {visibleSidePanelModuleIndex === SidePanelModulEnum.Settings && (
                    <>
                        <h2>
                            <i onClick={() => setVisibleSidePanelModuleIndex(SidePanelModulEnum.NONE)}>
                                <FontAwesomeIcon icon="times" />
                            </i>
                            Settings
                        </h2>
                        <ClearCacheWrapper>
                            <h4>Clear cache</h4>
                            <div>
                                <span>Click to clear cache and reload data. This will also reload the page.</span>
                                <button onClick={clearCacheAndLocalStorage}>Clear cache</button>
                            </div>
                        </ClearCacheWrapper>
                    </>
                )}
            </Wrapper>
        </>
    );
};

export default SidePanelPopupComponent;
